import type { StaffImportJob, StaffImportJobDetail } from "@lafayette/schemas";
import { useState } from "react";

import { factList } from "../../catalog/components/styles";
import { formatDateTime } from "../../catalog/labels";
import { CommandDialog } from "../../ui/command-dialog";
import { TextAreaField } from "../../ui/fields";
import { SectionCard } from "../../ui/section-card";
import { StatusBadge, type StatusTone } from "../../ui/status-badge";
import { describeJobStatus, formatBytes, mediaTypeLabels } from "../labels";
import { monoValue } from "./styles";

const jobTones: Readonly<Record<ReturnType<typeof describeJobStatus>["tone"], StatusTone>> = {
  draft: "neutral",
  submitted: "warning",
  done: "success",
  failed: "danger",
  cancelled: "neutral",
};

/** A job status as a glyph and a word: it never depends on colour alone. A long status wraps inside its cell. */
export function JobStatusBadge({
  job,
}: Readonly<{ job: Pick<StaffImportJob, "status" | "failureCode"> }>) {
  const view = describeJobStatus(job);
  return (
    <span data-job-status={view.tone} className="inline-flex max-w-full">
      <StatusBadge
        tone={jobTones[view.tone]}
        glyph={view.glyph}
        className="max-w-full whitespace-normal"
      >
        {view.label}
      </StatusBadge>
    </span>
  );
}

/**
 * What the server returned about a draft: file name, checksum, mapping revision and alias versions,
 * timestamps. The file name is untrusted display text: it is shown as text and never logged.
 */
export function DraftFacts({ detail }: Readonly<{ detail: StaffImportJobDetail }>) {
  const { job, mapping } = detail;
  return (
    <SectionCard id="import-facts" title="Draft details">
      <dl className={factList}>
        <div className="min-w-0">
          <dt>File name</dt>
          <dd className="min-w-0 font-semibold [overflow-wrap:anywhere]">{job.fileName}</dd>
        </div>
        <div className="min-w-0">
          <dt>Status</dt>
          <dd className="min-w-0">
            <JobStatusBadge job={job} />
          </dd>
        </div>
        <div>
          <dt>File type</dt>
          <dd>
            {mediaTypeLabels[job.mediaType]}, {formatBytes(job.byteSize)}
          </dd>
        </div>
        <div>
          <dt>Data rows</dt>
          <dd>{job.rowCount}</dd>
        </div>
        {mapping === null ? null : (
          <>
            <div>
              <dt>Mapping revision</dt>
              <dd>{mapping.revision}</dd>
            </div>
            <div>
              <dt>Alias versions</dt>
              <dd>
                Built-in {mapping.builtinAliasVersion}, learned {mapping.learnedAliasVersion}
              </dd>
            </div>
          </>
        )}
        <div className="col-span-full">
          <dt>Checksum (SHA-256)</dt>
          <dd className={monoValue}>{job.sourceSha256}</dd>
        </div>
        {mapping === null ? null : (
          <div>
            <dt>Mapping saved</dt>
            <dd>{formatDateTime(mapping.createdAt)}</dd>
          </div>
        )}
        <div>
          <dt>Created</dt>
          <dd>
            {formatDateTime(job.createdAt)}
            {job.createdByName === null ? "" : ` by ${job.createdByName}`}
          </dd>
        </div>
        <div>
          <dt>Last changed</dt>
          <dd>{formatDateTime(job.updatedAt)}</dd>
        </div>
      </dl>
    </SectionCard>
  );
}

type CancelDraftDialogProps = Readonly<{
  /** Runs the command. Returns an error sentence to show, or `null` when it worked (the dialog then closes). */
  onConfirm: (reason: string) => Promise<string | null>;
  onClose: () => void;
}>;

/** Asks why the draft is cancelled. The reason is staff text; it is sent to the server and not shown again here. */
export function CancelDraftDialog({ onConfirm, onClose }: CancelDraftDialogProps) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | undefined>();
  return (
    <CommandDialog
      title="Cancel this draft"
      description="The draft is closed and cannot be opened for changes again. No catalog item is changed."
      confirmLabel="Cancel draft"
      cancelLabel="Keep draft"
      tone="danger"
      order="confirm-first"
      validate={() => {
        if (reason.trim() === "") {
          setError("Enter a reason.");
          return false;
        }
        setError(undefined);
        return true;
      }}
      mutate={async () => {
        const failure = await onConfirm(reason.trim());
        return failure === null
          ? { ok: true, value: undefined }
          : { ok: false, failure: { message: failure, retryable: false, keepForm: true } };
      }}
      onDone={onClose}
      onClose={onClose}
    >
      <TextAreaField
        id="cancel-draft-reason"
        label="Reason"
        value={reason}
        onChange={setReason}
        error={error}
        required
        rows={3}
        maxLength={500}
      />
    </CommandDialog>
  );
}
